import React, { useEffect, useMemo, useRef } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import * as THREE from "three";

/* Life-like isometric smart home. All primitives, no external assets.
   Layout (top-down, x → right, z → toward viewer):
        Kitchen (back-left)    Bedroom (back-right)
        Living Room (front-l)  Entrance Porch (front-r)
   Gaps between rooms keep every surface visible in isometric view.
   Walls only on -x and -z sides of each room so the interior is never occluded. */

const NAVY = "#1B2E7A";
const CYAN = "#1BB6F0";
const WARM_YELLOW = "#FFCE7A";

const WALL_WARM = "#F3EEE7";
const WALL_COOL = "#E7EEF3";
const WALL_KITCHEN = "#EAF3E9";
const BASE_TRIM = "#FFFFFF";
const FLOOR_WOOD = "#B98A5B";
const FLOOR_WOOD_DARK = "#8F6339";
const FLOOR_BED = "#C69B6A";
const FLOOR_KITCHEN = "#D9C5A0";
const FLOOR_PORCH = "#8F6339";
const RUG_TONE = "#D9C5A6";
const CUSHION_NAVY = "#243B7A";
const CUSHION_CREAM = "#E8DDC6";
const FRAME_BLACK = "#111827";

/* ---------- Text label texture helper ---------- */
function useLabelTexture(text, opts = {}) {
    return useMemo(() => {
        const canvas = document.createElement("canvas");
        canvas.width = 512;
        canvas.height = 128;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = opts.bg || "rgba(255,255,255,0.92)";
        // rounded rect
        const r = 24, w = canvas.width, h = canvas.height;
        ctx.beginPath();
        ctx.moveTo(r, 0);
        ctx.arcTo(w, 0, w, h, r);
        ctx.arcTo(w, h, 0, h, r);
        ctx.arcTo(0, h, 0, 0, r);
        ctx.arcTo(0, 0, w, 0, r);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = opts.color || "#1B2E7A";
        ctx.font = `700 ${opts.size || 60}px Outfit, ui-sans-serif, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(text, w / 2, h / 2 + 4);
        const tex = new THREE.CanvasTexture(canvas);
        tex.anisotropy = 4;
        tex.needsUpdate = true;
        return tex;
    }, [text, opts.bg, opts.color, opts.size]);
}

function FloorLabel({ position, text, width = 1.6 }) {
    const tex = useLabelTexture(text);
    return (
        <mesh position={position} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[width, width * 0.25]} />
            <meshStandardMaterial map={tex} transparent />
        </mesh>
    );
}

/* ---------- Shell primitives ---------- */

function Wall({ position, size, color = WALL_WARM }) {
    const [x, y, z] = position;
    const [w, h, d] = size;
    return (
        <group position={[x, y, z]}>
            <mesh receiveShadow castShadow>
                <boxGeometry args={[w, h, d]} />
                <meshStandardMaterial color={color} roughness={0.95} />
            </mesh>
            {/* baseboard */}
            <mesh position={[0, -h / 2 + 0.06, d > w ? 0 : d / 2 + 0.001]}>
                <boxGeometry args={[w > d ? w + 0.01 : 0.02, 0.12, d > w ? d + 0.01 : 0.02]} />
                <meshStandardMaterial color={BASE_TRIM} />
            </mesh>
        </group>
    );
}

function Floor({ position, size, color = FLOOR_WOOD, plank = true }) {
    const [x, y, z] = position;
    const [w, d] = size;
    const planks = plank ? Math.floor(d / 0.55) : 0;
    return (
        <group position={[x, y, z]}>
            <mesh receiveShadow>
                <boxGeometry args={[w, 0.06, d]} />
                <meshStandardMaterial color={color} roughness={0.75} />
            </mesh>
            {plank && Array.from({ length: planks }).map((_, i) => (
                <mesh key={i} position={[0, 0.032, -d / 2 + 0.55 * (i + 1)]}>
                    <boxGeometry args={[w, 0.005, 0.012]} />
                    <meshStandardMaterial color={FLOOR_WOOD_DARK} opacity={0.55} transparent />
                </mesh>
            ))}
        </group>
    );
}

function Rug({ position, size, color = RUG_TONE }) {
    const [x, y, z] = position;
    const [w, d] = size;
    return (
        <group position={[x, y + 0.001, z]}>
            <mesh>
                <boxGeometry args={[w, 0.008, d]} />
                <meshStandardMaterial color={color} roughness={1} />
            </mesh>
            <mesh position={[0, 0.005, 0]}>
                <boxGeometry args={[w - 0.14, 0.008, d - 0.14]} />
                <meshStandardMaterial color="#C4A87D" roughness={1} />
            </mesh>
        </group>
    );
}

/* ---------- Smart devices ---------- */

function RecessedLight({ position, on }) {
    return (
        <group position={position}>
            <mesh>
                <cylinderGeometry args={[0.11, 0.11, 0.03, 20]} />
                <meshStandardMaterial color="#F1F5F9" />
            </mesh>
            <mesh position={[0, -0.015, 0]}>
                <cylinderGeometry args={[0.09, 0.09, 0.02, 20]} />
                <meshStandardMaterial color={on ? WARM_YELLOW : "#94A3B8"} emissive={on ? WARM_YELLOW : "#000"} emissiveIntensity={on ? 2.2 : 0} />
            </mesh>
            {on && (
                <mesh position={[0, -0.6, 0]}>
                    <coneGeometry args={[0.55, 1.1, 24, 1, true]} />
                    <meshBasicMaterial color={WARM_YELLOW} transparent opacity={0.18} side={THREE.DoubleSide} />
                </mesh>
            )}
            {on && <pointLight color={WARM_YELLOW} intensity={0.35} distance={2.5} position={[0, -0.2, 0]} />}
        </group>
    );
}

function LEDStrip({ position, length = 3, on, color = CYAN }) {
    const [x, y, z] = position;
    return (
        <group position={[x, y, z]}>
            <mesh><boxGeometry args={[length, 0.03, 0.03]} /><meshStandardMaterial color="#111827" /></mesh>
            <mesh position={[0, -0.02, 0]}>
                <boxGeometry args={[length, 0.005, 0.02]} />
                <meshStandardMaterial color={on ? color : "#334155"} emissive={on ? color : "#000"} emissiveIntensity={on ? 1.6 : 0} />
            </mesh>
        </group>
    );
}

/* Proper ceiling fan: motor housing + light kit + 4 blades attached via nested rotation groups so all 4 rotate together correctly. Blades have a slight pitch. */
function CeilingFan({ position, on }) {
    const bladesRef = useRef();
    useFrame((_, dt) => {
        if (!bladesRef.current) return;
        if (on) bladesRef.current.rotation.y += dt * 6;
    });
    const angles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];
    return (
        <group position={position}>
            {/* Downrod */}
            <mesh position={[0, 0.14, 0]}>
                <cylinderGeometry args={[0.025, 0.025, 0.28, 12]} />
                <meshStandardMaterial color="#8B9199" metalness={0.4} roughness={0.5} />
            </mesh>
            {/* Motor housing (top disc) */}
            <mesh position={[0, 0, 0]}>
                <cylinderGeometry args={[0.16, 0.14, 0.12, 24]} />
                <meshStandardMaterial color="#E8ECF1" metalness={0.5} roughness={0.4} />
            </mesh>
            {/* Motor lower cover */}
            <mesh position={[0, -0.08, 0]}>
                <cylinderGeometry args={[0.13, 0.11, 0.06, 24]} />
                <meshStandardMaterial color="#5B6470" metalness={0.6} roughness={0.35} />
            </mesh>
            {/* Blades (rotate together) */}
            <group ref={bladesRef} position={[0, -0.02, 0]}>
                {angles.map((a, i) => (
                    <group key={i} rotation={[0, a, 0]}>
                        {/* Blade holder / arm */}
                        <mesh position={[0.18, 0, 0]}>
                            <boxGeometry args={[0.12, 0.02, 0.05]} />
                            <meshStandardMaterial color="#5B6470" metalness={0.5} roughness={0.35} />
                        </mesh>
                        {/* Blade with slight pitch (rotate around z axis) */}
                        <group rotation={[0, 0, 0.16]}>
                            <mesh position={[0.75, 0, 0]}>
                                <boxGeometry args={[1.15, 0.012, 0.18]} />
                                <meshStandardMaterial color="#E9ECF1" roughness={0.5} />
                            </mesh>
                            {/* Blade grain highlight */}
                            <mesh position={[0.75, 0.008, 0]}>
                                <boxGeometry args={[1.1, 0.001, 0.02]} />
                                <meshStandardMaterial color="#C7CDD6" />
                            </mesh>
                        </group>
                    </group>
                ))}
                {/* Motion disc when running */}
                {on && (
                    <mesh position={[0, -0.01, 0]} rotation={[Math.PI / 2, 0, 0]}>
                        <ringGeometry args={[0.35, 1.35, 40]} />
                        <meshBasicMaterial color="#B7C1CE" transparent opacity={0.12} side={THREE.DoubleSide} />
                    </mesh>
                )}
            </group>
            {/* Light kit under motor */}
            <mesh position={[0, -0.16, 0]}>
                <sphereGeometry args={[0.09, 20, 20]} />
                <meshStandardMaterial color={on ? WARM_YELLOW : "#DFE4EB"} emissive={on ? WARM_YELLOW : "#000"} emissiveIntensity={on ? 1.2 : 0} />
            </mesh>
        </group>
    );
}

/* Wall-mounted TV mounted flat against a -z wall (screen faces +z). */
function TVSet({ position, on }) {
    const screenRef = useRef();
    useFrame(() => {
        if (!screenRef.current || !on) return;
        const t = performance.now() * 0.001;
        const hue = 0.55 + Math.sin(t * 0.6) * 0.05;
        screenRef.current.material.color.setHSL(hue, 0.7, 0.5);
        screenRef.current.material.emissive.setHSL(hue, 0.7, 0.42);
    });
    return (
        <group position={position}>
            <mesh position={[0, 0, 0.02]}>
                <boxGeometry args={[1.62, 1.0, 0.05]} />
                <meshStandardMaterial color={FRAME_BLACK} />
            </mesh>
            <mesh position={[0, 0, 0.055]} ref={screenRef}>
                <boxGeometry args={[1.5, 0.88, 0.01]} />
                <meshStandardMaterial color={on ? CYAN : "#1E293B"} emissive={on ? CYAN : "#000"} emissiveIntensity={on ? 1.4 : 0} />
            </mesh>
            {/* Console */}
            <mesh position={[0, -0.72, 0.24]}>
                <boxGeometry args={[1.8, 0.35, 0.45]} />
                <meshStandardMaterial color="#5B4636" roughness={0.6} />
            </mesh>
            <mesh position={[0, -0.7, 0.47]}>
                <boxGeometry args={[1.7, 0.28, 0.02]} />
                <meshStandardMaterial color="#3F2A18" />
            </mesh>
        </group>
    );
}

/* Split AC — body flat against a wall. `orient` decides which axis the vents face.
   When `on`, emits a visible cool-air stream (translucent cyan cone), a bright pulsing
   LED indicator and a small "22°C" digital display so users see the AC actually reacting. */
function SplitAC({ position, on, orient = "z" }) {
    const rot = orient === "-x" ? [0, Math.PI / 2, 0] : [0, 0, 0];
    const ledRef = useRef();
    const streamRef = useRef();
    useFrame(() => {
        if (!on) return;
        const t = performance.now() * 0.001;
        // Pulse LED
        if (ledRef.current) {
            ledRef.current.material.emissiveIntensity = 1.5 + Math.sin(t * 4) * 0.9;
        }
        // Subtly breathe the airflow
        if (streamRef.current) {
            streamRef.current.material.opacity = 0.18 + Math.sin(t * 2) * 0.06;
        }
    });
    return (
        <group position={position} rotation={rot}>
            {/* Body */}
            <mesh castShadow>
                <boxGeometry args={[1.5, 0.34, 0.34]} />
                <meshStandardMaterial color="#FFFFFF" roughness={0.55} />
            </mesh>
            {/* Vent slats */}
            {Array.from({ length: 7 }).map((_, i) => (
                <mesh key={i} position={[0, -0.03 + i * 0.008, 0.18]}>
                    <boxGeometry args={[1.4, 0.014, 0.005]} />
                    <meshStandardMaterial color="#CBD5E1" />
                </mesh>
            ))}
            {/* Bottom outlet */}
            <mesh position={[0, -0.15, 0.16]}>
                <boxGeometry args={[1.4, 0.04, 0.06]} />
                <meshStandardMaterial color="#94A3B8" />
            </mesh>
            {/* Front logo bar */}
            <mesh position={[-0.55, 0.02, 0.18]}>
                <boxGeometry args={[0.14, 0.03, 0.003]} />
                <meshStandardMaterial color="#334155" />
            </mesh>
            {/* Digital display "22°C" (represented as a bright cyan bar when on) */}
            <mesh position={[0.4, 0.05, 0.18]}>
                <boxGeometry args={[0.22, 0.09, 0.006]} />
                <meshStandardMaterial color={on ? "#0F172A" : "#334155"} />
            </mesh>
            <mesh position={[0.4, 0.05, 0.185]}>
                <boxGeometry args={[0.18, 0.055, 0.003]} />
                <meshStandardMaterial
                    color={on ? CYAN : "#334155"}
                    emissive={on ? CYAN : "#000"}
                    emissiveIntensity={on ? 1.6 : 0}
                />
            </mesh>
            {/* Big pulsing LED indicator */}
            <mesh ref={ledRef} position={[0.62, 0, 0.19]}>
                <sphereGeometry args={[0.03, 12, 12]} />
                <meshStandardMaterial
                    color={on ? CYAN : "#94A3B8"}
                    emissive={on ? CYAN : "#000"}
                    emissiveIntensity={on ? 2 : 0}
                />
            </mesh>
        </group>
    );
}

/* Realistic window mounted flat against a wall. `orient` selects wall axis. Includes frame, mullions, sky pane. */
function Window({ position, size = [1.8, 1.0], orient = "z" }) {
    const rot = orient === "-x" ? [0, Math.PI / 2, 0] : [0, 0, 0];
    const [w, h] = size;
    return (
        <group position={position} rotation={rot}>
            {/* Sky (bright, distant) */}
            <mesh position={[0, 0, 0.005]}>
                <boxGeometry args={[w, h, 0.005]} />
                <meshStandardMaterial color="#BFE3F5" emissive="#BFE3F5" emissiveIntensity={0.35} />
            </mesh>
            {/* Distant hills */}
            <mesh position={[0, -h * 0.15, 0.006]}>
                <boxGeometry args={[w * 0.9, h * 0.25, 0.001]} />
                <meshStandardMaterial color="#7FB7A1" />
            </mesh>
            {/* Frame outer */}
            <mesh position={[0, h / 2 + 0.02, 0.01]}>
                <boxGeometry args={[w + 0.14, 0.06, 0.05]} />
                <meshStandardMaterial color="#FFFFFF" />
            </mesh>
            <mesh position={[0, -h / 2 - 0.02, 0.01]}>
                <boxGeometry args={[w + 0.14, 0.06, 0.05]} />
                <meshStandardMaterial color="#FFFFFF" />
            </mesh>
            <mesh position={[-w / 2 - 0.03, 0, 0.01]}>
                <boxGeometry args={[0.06, h + 0.14, 0.05]} />
                <meshStandardMaterial color="#FFFFFF" />
            </mesh>
            <mesh position={[w / 2 + 0.03, 0, 0.01]}>
                <boxGeometry args={[0.06, h + 0.14, 0.05]} />
                <meshStandardMaterial color="#FFFFFF" />
            </mesh>
            {/* Mullion cross */}
            <mesh position={[0, 0, 0.015]}>
                <boxGeometry args={[w, 0.03, 0.008]} />
                <meshStandardMaterial color="#FFFFFF" />
            </mesh>
            <mesh position={[0, 0, 0.015]}>
                <boxGeometry args={[0.03, h, 0.008]} />
                <meshStandardMaterial color="#FFFFFF" />
            </mesh>
            {/* Sill */}
            <mesh position={[0, -h / 2 - 0.08, 0.05]}>
                <boxGeometry args={[w + 0.24, 0.05, 0.14]} />
                <meshStandardMaterial color="#F1F5F9" />
            </mesh>
        </group>
    );
}

/* Curtains: two side panels that FRAME the window. When CLOSED they meet in the middle to fully cover the window; when OPEN they retract to the sides. Off-white / light-grey fabric look via alternating fold slabs. */
function CurtainSet({ position, open, width = 2.0, height = 1.4, orient = "z" }) {
    const rot = orient === "-x" ? [0, Math.PI / 2, 0] : [0, 0, 0];
    const drapeFolds = 6;
    // Closed: each panel covers half the window (meeting in the middle). Open: panels tucked to sides.
    const panelWidth = open ? width * 0.16 : width * 0.52;
    return (
        <group position={position} rotation={rot}>
            {/* Rod */}
            <mesh rotation={[0, 0, Math.PI / 2]}>
                <cylinderGeometry args={[0.028, 0.028, width + 0.24, 14]} />
                <meshStandardMaterial color="#334155" metalness={0.5} roughness={0.4} />
            </mesh>
            {[-1, 1].map((s) => (
                <mesh key={s} position={[s * (width / 2 + 0.1), 0, 0]}>
                    <sphereGeometry args={[0.05, 12, 12]} />
                    <meshStandardMaterial color={NAVY} />
                </mesh>
            ))}
            {/* Two side panels, each with alternating folds */}
            {[-1, 1].map((side) => (
                <group key={side} position={[side * (width / 2 - panelWidth / 2), -height / 2 - 0.05, 0.02]}>
                    {Array.from({ length: drapeFolds }).map((_, i) => {
                        const localX = -panelWidth / 2 + (i + 0.5) * (panelWidth / drapeFolds);
                        const isRidge = i % 2 === 0;
                        const depthShift = isRidge ? 0.014 : -0.004;
                        return (
                            <mesh key={i} position={[localX, 0, depthShift]}>
                                <boxGeometry args={[(panelWidth / drapeFolds) * 0.99, height, isRidge ? 0.035 : 0.02]} />
                                <meshStandardMaterial
                                    color={isRidge ? "#F2ECDE" : "#D8D2C1"}
                                    roughness={1}
                                    metalness={0}
                                />
                            </mesh>
                        );
                    })}
                    {/* Slight scallop at hem — small darker strip at bottom */}
                    <mesh position={[0, -height / 2 + 0.02, 0.015]}>
                        <boxGeometry args={[panelWidth * 0.99, 0.04, 0.02]} />
                        <meshStandardMaterial color="#BFB6A0" />
                    </mesh>
                    {/* Tie-back ring when open */}
                    {open && (
                        <mesh position={[side > 0 ? -panelWidth / 2 - 0.02 : panelWidth / 2 + 0.02, -height * 0.1, 0.03]}>
                            <torusGeometry args={[0.05, 0.012, 8, 16]} />
                            <meshStandardMaterial color={NAVY} metalness={0.4} />
                        </mesh>
                    )}
                </group>
            ))}
        </group>
    );
}

/* Front door with side panels, handle, keypad. Placed under a lintel. */
function FrontDoor({ position, locked }) {
    return (
        <group position={position}>
            {/* Lintel */}
            <mesh position={[0, 1.05, 0]}>
                <boxGeometry args={[1.4, 0.14, 0.24]} />
                <meshStandardMaterial color="#3F2A18" />
            </mesh>
            {/* Side frames */}
            {[-1, 1].map((s) => (
                <mesh key={s} position={[s * 0.5, 0.15, 0]}>
                    <boxGeometry args={[0.14, 1.85, 0.18]} />
                    <meshStandardMaterial color="#3F2A18" />
                </mesh>
            ))}
            {/* Door slab */}
            <mesh position={[0, 0.05, 0]}>
                <boxGeometry args={[0.82, 1.85, 0.08]} />
                <meshStandardMaterial color={NAVY} roughness={0.4} />
            </mesh>
            {[0.45, -0.05, -0.55].map((y, i) => (
                <mesh key={i} position={[0, y, 0.05]}>
                    <boxGeometry args={[0.52, 0.32, 0.012]} />
                    <meshStandardMaterial color="#152462" />
                </mesh>
            ))}
            {/* Handle */}
            <mesh position={[0.26, 0.05, 0.06]}>
                <cylinderGeometry args={[0.045, 0.045, 0.18, 12]} rotation={[Math.PI / 2, 0, 0]} />
                <meshStandardMaterial color="#D4AF37" metalness={0.85} roughness={0.15} />
            </mesh>
            {/* Smart lock keypad */}
            <mesh position={[0.26, -0.18, 0.06]}>
                <boxGeometry args={[0.14, 0.22, 0.02]} />
                <meshStandardMaterial color="#0F172A" />
            </mesh>
            <mesh position={[0.26, -0.13, 0.075]}>
                <boxGeometry args={[0.1, 0.05, 0.005]} />
                <meshStandardMaterial color={locked ? "#EF4444" : CYAN} emissive={locked ? "#EF4444" : CYAN} emissiveIntensity={1.4} />
            </mesh>
            {/* Keypad dots */}
            {[0, 1, 2].map((i) => (
                <mesh key={i} position={[0.22 + i * 0.03, -0.22, 0.075]}>
                    <sphereGeometry args={[0.008, 6, 6]} />
                    <meshStandardMaterial color="#94A3B8" />
                </mesh>
            ))}
        </group>
    );
}

/* Interior doorway: visible wooden frame + a door slab shown slightly ajar (opening inward). `swingLeft` sets the hinge side. */
function Doorway({ position, orient = "z", swingLeft = true }) {
    const rot = orient === "-x" ? [0, Math.PI / 2, 0] : [0, 0, 0];
    const hingeSign = swingLeft ? -1 : 1;
    // Ajar angle ~35° opening inward (into the -z direction of the doorway's local frame).
    const ajar = swingLeft ? 0.62 : -0.62;
    return (
        <group position={position} rotation={rot}>
            {/* Lintel */}
            <mesh position={[0, 1.05, 0]}>
                <boxGeometry args={[1.35, 0.16, 0.22]} />
                <meshStandardMaterial color="#3F2A18" />
            </mesh>
            {/* Side jambs */}
            {[-1, 1].map((s) => (
                <mesh key={s} position={[s * 0.62, 0.15, 0]}>
                    <boxGeometry args={[0.12, 1.88, 0.2]} />
                    <meshStandardMaterial color="#3F2A18" />
                </mesh>
            ))}
            {/* Threshold */}
            <mesh position={[0, -0.79, 0]}>
                <boxGeometry args={[1.24, 0.03, 0.22]} />
                <meshStandardMaterial color="#8F6339" />
            </mesh>
            {/* Ajar door slab hinged on `hingeSign` side, swinging inward (rotation around Y) */}
            <group position={[hingeSign * 0.55, 0.15, 0]}>
                <group rotation={[0, ajar, 0]}>
                    {/* slab centered offset so hinge is at edge */}
                    <mesh castShadow position={[-hingeSign * 0.4, 0, 0.05]}>
                        <boxGeometry args={[0.8, 1.72, 0.06]} />
                        <meshStandardMaterial color="#F0E6D2" roughness={0.55} />
                    </mesh>
                    {/* Two inset door panels */}
                    <mesh position={[-hingeSign * 0.4, 0.42, 0.09]}>
                        <boxGeometry args={[0.5, 0.32, 0.012]} />
                        <meshStandardMaterial color="#DDCDA8" />
                    </mesh>
                    <mesh position={[-hingeSign * 0.4, -0.32, 0.09]}>
                        <boxGeometry args={[0.5, 0.42, 0.012]} />
                        <meshStandardMaterial color="#DDCDA8" />
                    </mesh>
                    {/* Handle on far edge */}
                    <mesh position={[-hingeSign * 0.72, 0, 0.09]} rotation={[Math.PI / 2, 0, 0]}>
                        <cylinderGeometry args={[0.028, 0.028, 0.14, 12]} />
                        <meshStandardMaterial color="#D4AF37" metalness={0.85} roughness={0.15} />
                    </mesh>
                </group>
            </group>
        </group>
    );
}

function SecurityCamera({ position, on }) {
    return (
        <group position={position}>
            <mesh position={[0, 0, -0.08]}>
                <boxGeometry args={[0.06, 0.08, 0.14]} />
                <meshStandardMaterial color="#94A3B8" />
            </mesh>
            <mesh position={[0, 0, 0.04]} rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.09, 0.09, 0.22, 20]} />
                <meshStandardMaterial color="#FFFFFF" roughness={0.55} />
            </mesh>
            <mesh position={[0, 0, 0.16]}>
                <sphereGeometry args={[0.06, 16, 16]} />
                <meshStandardMaterial color={FRAME_BLACK} />
            </mesh>
            <mesh position={[0.05, 0.07, 0.16]}>
                <sphereGeometry args={[0.015, 8, 8]} />
                <meshStandardMaterial color={on ? "#EF4444" : "#334155"} emissive={on ? "#EF4444" : "#000"} emissiveIntensity={on ? 2 : 0} />
            </mesh>
        </group>
    );
}

function Doorbell({ position, on }) {
    return (
        <group position={position}>
            <mesh><boxGeometry args={[0.1, 0.18, 0.03]} /><meshStandardMaterial color="#F1F5F9" /></mesh>
            <mesh position={[0, 0.04, 0.02]}>
                <sphereGeometry args={[0.022, 12, 12]} />
                <meshStandardMaterial color={on ? CYAN : "#94A3B8"} emissive={on ? CYAN : "#000"} emissiveIntensity={on ? 2 : 0} />
            </mesh>
            <mesh position={[0, -0.03, 0.018]}>
                <boxGeometry args={[0.06, 0.06, 0.005]} />
                <meshStandardMaterial color="#0F172A" />
            </mesh>
        </group>
    );
}

function PorchLight({ position, on }) {
    return (
        <group position={position}>
            <mesh><boxGeometry args={[0.08, 0.16, 0.14]} /><meshStandardMaterial color="#0F172A" /></mesh>
            <mesh position={[0, -0.14, 0]}>
                <sphereGeometry args={[0.09, 14, 14]} />
                <meshStandardMaterial color={on ? WARM_YELLOW : "#94A3B8"} emissive={on ? WARM_YELLOW : "#000"} emissiveIntensity={on ? 2 : 0} />
            </mesh>
            {on && <pointLight color={WARM_YELLOW} intensity={0.6} distance={2.5} position={[0, -0.2, 0]} />}
        </group>
    );
}

/* ---------- Furniture ---------- */

/* Sofa: seat faces +z (cushions on +z side). Backrest on -z side of local origin. */
function Sofa({ position, rotation = [0, 0, 0] }) {
    return (
        <group position={position} rotation={rotation}>
            {/* base */}
            <mesh castShadow position={[0, 0.2, 0]}>
                <boxGeometry args={[2.2, 0.35, 0.95]} />
                <meshStandardMaterial color="#D9DDE3" roughness={0.9} />
            </mesh>
            {/* backrest (on -z side) */}
            <mesh castShadow position={[0, 0.6, -0.38]}>
                <boxGeometry args={[2.2, 0.65, 0.22]} />
                <meshStandardMaterial color="#D9DDE3" roughness={0.9} />
            </mesh>
            {/* armrests */}
            {[-1.05, 1.05].map((x, i) => (
                <mesh key={i} castShadow position={[x, 0.45, 0]}>
                    <boxGeometry args={[0.18, 0.42, 0.95]} />
                    <meshStandardMaterial color="#C8CCD3" roughness={0.9} />
                </mesh>
            ))}
            {/* seat cushions (on +z side of base) */}
            {[-0.7, 0, 0.7].map((x, i) => (
                <mesh key={`c${i}`} castShadow position={[x, 0.44, 0.05]}>
                    <boxGeometry args={[0.62, 0.14, 0.82]} />
                    <meshStandardMaterial color={i === 1 ? CUSHION_NAVY : "#E4E8ED"} roughness={0.95} />
                </mesh>
            ))}
            {/* throw pillows against backrest */}
            <mesh position={[-0.85, 0.66, -0.24]}><boxGeometry args={[0.32, 0.32, 0.14]} /><meshStandardMaterial color={CUSHION_NAVY} /></mesh>
            <mesh position={[0.85, 0.66, -0.24]}><boxGeometry args={[0.32, 0.32, 0.14]} /><meshStandardMaterial color={CUSHION_CREAM} /></mesh>
        </group>
    );
}

function CoffeeTable({ position }) {
    return (
        <group position={position}>
            <mesh castShadow position={[0, 0.24, 0]}>
                <boxGeometry args={[1.1, 0.05, 0.55]} />
                <meshStandardMaterial color="#5B4636" roughness={0.5} />
            </mesh>
            {[[-0.5, 0.12, -0.24], [0.5, 0.12, -0.24], [-0.5, 0.12, 0.24], [0.5, 0.12, 0.24]].map((p, i) => (
                <mesh key={i} position={p}><boxGeometry args={[0.04, 0.24, 0.04]} /><meshStandardMaterial color="#3F2A18" /></mesh>
            ))}
            <mesh position={[0.2, 0.34, 0]}><cylinderGeometry args={[0.06, 0.08, 0.16, 16]} /><meshStandardMaterial color="#FFFFFF" /></mesh>
            <mesh position={[0.2, 0.5, 0]}><sphereGeometry args={[0.09, 12, 12]} /><meshStandardMaterial color="#DA9B6D" /></mesh>
            <mesh position={[-0.25, 0.29, 0.05]}><boxGeometry args={[0.32, 0.03, 0.2]} /><meshStandardMaterial color={NAVY} /></mesh>
        </group>
    );
}

function Bed({ position, rotation = [0, 0, 0] }) {
    // Headboard on -z side of local coords
    return (
        <group position={position} rotation={rotation}>
            <mesh position={[0, 0.14, 0]}><boxGeometry args={[1.9, 0.28, 2.2]} /><meshStandardMaterial color="#3F2A18" roughness={0.5} /></mesh>
            <mesh castShadow position={[0, 0.36, 0]}><boxGeometry args={[1.8, 0.18, 2.05]} /><meshStandardMaterial color="#FFFFFF" roughness={0.9} /></mesh>
            {/* Duvet */}
            <mesh position={[0, 0.44, 0.3]}><boxGeometry args={[1.75, 0.06, 1.45]} /><meshStandardMaterial color="#EEEAE0" roughness={1} /></mesh>
            <mesh position={[0, 0.5, -0.4]}><boxGeometry args={[1.75, 0.14, 0.35]} /><meshStandardMaterial color="#DCD3C1" roughness={1} /></mesh>
            {[-0.5, 0.5].map((x, i) => (
                <mesh key={i} position={[x, 0.5, -0.78]}><boxGeometry args={[0.65, 0.14, 0.35]} /><meshStandardMaterial color="#FFFFFF" /></mesh>
            ))}
            <mesh position={[0, 0.75, -1.05]}><boxGeometry args={[1.95, 0.85, 0.12]} /><meshStandardMaterial color={NAVY} /></mesh>
        </group>
    );
}

function Nightstand({ position, on }) {
    return (
        <group position={position}>
            <mesh castShadow position={[0, 0.22, 0]}><boxGeometry args={[0.44, 0.44, 0.4]} /><meshStandardMaterial color="#5B4636" /></mesh>
            <mesh position={[0, 0.22, 0.21]}><boxGeometry args={[0.36, 0.36, 0.005]} /><meshStandardMaterial color="#3F2A18" /></mesh>
            <mesh position={[0, 0.5, 0]}><cylinderGeometry args={[0.02, 0.02, 0.15, 8]} /><meshStandardMaterial color="#334155" /></mesh>
            <mesh position={[0, 0.62, 0]} rotation={[Math.PI, 0, 0]}>
                <coneGeometry args={[0.12, 0.14, 16, 1, true]} />
                <meshStandardMaterial color={on ? WARM_YELLOW : "#E5E7EB"} emissive={on ? WARM_YELLOW : "#000"} emissiveIntensity={on ? 1.4 : 0} side={THREE.DoubleSide} />
            </mesh>
        </group>
    );
}

/* Wardrobe with doors facing +z (front) by default. Use rotation prop to change. */
function Wardrobe({ position, rotation = [0, 0, 0] }) {
    return (
        <group position={position} rotation={rotation}>
            <mesh castShadow position={[0, 0.9, 0]}><boxGeometry args={[1.4, 1.8, 0.55]} /><meshStandardMaterial color="#EBE3D3" roughness={0.7} /></mesh>
            {/* Middle door divider */}
            <mesh position={[0, 0.9, 0.28]}><boxGeometry args={[0.015, 1.72, 0.005]} /><meshStandardMaterial color="#94A3B8" /></mesh>
            {/* Two door panels with slight inset */}
            {[-0.34, 0.34].map((x, i) => (
                <mesh key={i} position={[x, 0.9, 0.281]}>
                    <boxGeometry args={[0.6, 1.65, 0.005]} />
                    <meshStandardMaterial color="#F4EDDD" />
                </mesh>
            ))}
            {/* Handles */}
            {[-0.1, 0.1].map((x, i) => (
                <mesh key={i} position={[x, 0.9, 0.29]}>
                    <boxGeometry args={[0.03, 0.16, 0.02]} />
                    <meshStandardMaterial color="#D4AF37" metalness={0.7} roughness={0.3} />
                </mesh>
            ))}
            {/* Base */}
            <mesh position={[0, 0.04, 0]}><boxGeometry args={[1.4, 0.08, 0.55]} /><meshStandardMaterial color="#3F2A18" /></mesh>
        </group>
    );
}

function WallArt({ position, orient = "z", color = CYAN }) {
    const rot = orient === "-x" ? [0, Math.PI / 2, 0] : [0, 0, 0];
    return (
        <group position={position} rotation={rot}>
            <mesh><boxGeometry args={[0.7, 0.5, 0.02]} /><meshStandardMaterial color="#FFFFFF" /></mesh>
            <mesh position={[0, 0, 0.011]}><boxGeometry args={[0.6, 0.4, 0.005]} /><meshStandardMaterial color={color} /></mesh>
            <mesh position={[0, 0, 0.014]}><boxGeometry args={[0.2, 0.4, 0.005]} /><meshStandardMaterial color={NAVY} /></mesh>
        </group>
    );
}

function Plant({ position, size = 1 }) {
    return (
        <group position={position} scale={size}>
            <mesh position={[0, 0.12, 0]}><cylinderGeometry args={[0.13, 0.1, 0.24, 16]} /><meshStandardMaterial color="#8A6A4B" /></mesh>
            <mesh position={[0, 0.42, 0]}><sphereGeometry args={[0.24, 12, 12]} /><meshStandardMaterial color="#3F8B5B" roughness={1} /></mesh>
            <mesh position={[0.09, 0.55, 0.03]}><sphereGeometry args={[0.14, 10, 10]} /><meshStandardMaterial color="#4E9C69" /></mesh>
            <mesh position={[-0.11, 0.5, -0.05]}><sphereGeometry args={[0.13, 10, 10]} /><meshStandardMaterial color="#348256" /></mesh>
        </group>
    );
}

function Doormat({ position }) {
    return (
        <group position={position}>
            <mesh><boxGeometry args={[0.9, 0.02, 0.5]} /><meshStandardMaterial color="#4B5D3E" /></mesh>
            <mesh position={[0, 0.015, 0]}><boxGeometry args={[0.7, 0.008, 0.3]} /><meshStandardMaterial color="#6F8451" /></mesh>
        </group>
    );
}

/* ---------- Kitchen furniture ---------- */

function Fridge({ position }) {
    return (
        <group position={position}>
            <mesh castShadow position={[0, 0.9, 0]}>
                <boxGeometry args={[0.7, 1.8, 0.6]} />
                <meshStandardMaterial color="#E6ECF1" metalness={0.55} roughness={0.35} />
            </mesh>
            {/* door split */}
            <mesh position={[0, 1.35, 0.31]}><boxGeometry args={[0.62, 0.02, 0.005]} /><meshStandardMaterial color="#94A3B8" /></mesh>
            {/* handles */}
            {[1.4, 0.6].map((y, i) => (
                <mesh key={i} position={[0.28, y, 0.32]}>
                    <boxGeometry args={[0.03, 0.28, 0.02]} />
                    <meshStandardMaterial color="#334155" />
                </mesh>
            ))}
            {/* Small display */}
            <mesh position={[-0.15, 1.55, 0.31]}>
                <boxGeometry args={[0.14, 0.08, 0.005]} />
                <meshStandardMaterial color="#0F172A" emissive={CYAN} emissiveIntensity={0.3} />
            </mesh>
        </group>
    );
}

function KitchenCounter({ position, size = [3, 0.6] }) {
    const [w, d] = size;
    return (
        <group position={position}>
            {/* cabinets */}
            <mesh position={[0, 0.42, 0]}>
                <boxGeometry args={[w, 0.84, d]} />
                <meshStandardMaterial color="#F4EEE0" roughness={0.7} />
            </mesh>
            {/* countertop */}
            <mesh position={[0, 0.87, 0]}>
                <boxGeometry args={[w + 0.05, 0.06, d + 0.05]} />
                <meshStandardMaterial color="#1F2937" roughness={0.3} metalness={0.2} />
            </mesh>
            {/* cabinet handles */}
            {[-w / 3, 0, w / 3].map((x, i) => (
                <mesh key={i} position={[x, 0.38, d / 2 + 0.02]}>
                    <boxGeometry args={[0.16, 0.02, 0.02]} />
                    <meshStandardMaterial color="#334155" />
                </mesh>
            ))}
        </group>
    );
}

function GasHob({ position }) {
    return (
        <group position={position}>
            <mesh><boxGeometry args={[0.7, 0.02, 0.5]} /><meshStandardMaterial color="#0F172A" /></mesh>
            {[[-0.16, -0.13], [0.16, -0.13], [-0.16, 0.13], [0.16, 0.13]].map((p, i) => (
                <mesh key={i} position={[p[0], 0.015, p[1]]}>
                    <cylinderGeometry args={[0.08, 0.08, 0.02, 20]} />
                    <meshStandardMaterial color="#334155" />
                </mesh>
            ))}
        </group>
    );
}

function Sink({ position }) {
    return (
        <group position={position}>
            <mesh><boxGeometry args={[0.6, 0.02, 0.4]} /><meshStandardMaterial color="#D4D9DF" metalness={0.7} roughness={0.25} /></mesh>
            <mesh position={[0, -0.04, 0]}><boxGeometry args={[0.48, 0.06, 0.3]} /><meshStandardMaterial color="#94A3B8" /></mesh>
            {/* faucet */}
            <mesh position={[0, 0.14, -0.16]}><cylinderGeometry args={[0.02, 0.02, 0.24, 8]} /><meshStandardMaterial color="#94A3B8" metalness={0.7} /></mesh>
            <mesh position={[0, 0.24, -0.1]}><boxGeometry args={[0.02, 0.02, 0.14]} /><meshStandardMaterial color="#94A3B8" metalness={0.7} /></mesh>
        </group>
    );
}

/* ---------- Scene ---------- */

function CameraRig() {
    const { camera, size } = useThree();
    useEffect(() => {
        camera.position.set(17, 15, 17);
        camera.lookAt(0, 0.4, 0);
        // Dynamic zoom: fit the scene's ~14-unit extent into the shorter canvas dimension.
        // Ensures the isometric house renders correctly on mobile portrait, tablet and desktop.
        const shorter = Math.min(size.width, size.height);
        const zoom = Math.max(22, Math.min(52, shorter / 14));
        camera.zoom = zoom;
        camera.updateProjectionMatrix();
    }, [camera, size.width, size.height]);
    return null;
}

export default function HouseScene({ state }) {
    const s = state;

    return (
        <Canvas
            shadows
            dpr={[1, 2]}
            orthographic
            camera={{ position: [17, 15, 17], zoom: 38, near: 0.1, far: 100 }}
            data-testid="house-canvas"
        >
            <CameraRig />
            <color attach="background" args={["#0A122E"]} />
            <fog attach="fog" args={["#0A122E", 26, 46]} />
            <ambientLight intensity={0.6} />
            <hemisphereLight args={["#ffffff", "#a8b4d8", 0.55]} />
            <directionalLight
                position={[10, 14, 8]}
                intensity={1.15}
                castShadow
                shadow-mapSize-width={2048}
                shadow-mapSize-height={2048}
                shadow-camera-left={-14}
                shadow-camera-right={14}
                shadow-camera-top={14}
                shadow-camera-bottom={-14}
            />

            {/* Base slab (dark border under all rooms) */}
            <mesh position={[0, -0.12, 0]} receiveShadow>
                <boxGeometry args={[16, 0.04, 14]} />
                <meshStandardMaterial color="#0E1A4A" />
            </mesh>

            {/* =====================================================
                LIVING ROOM (front-left)   x ∈ [-5.5, -1.5],  z ∈ [0.5, 4]
                Walls on -x and -z (back-left corner)
               ===================================================== */}
            <group position={[-3.5, 0, 2.25]}>
                <Floor position={[0, 0, 0]} size={[4, 3.5]} />
                <Wall position={[0, 1.25, -1.75]} size={[4, 2.5, 0.08]} color={WALL_WARM} />
                <Wall position={[-2, 1.25, 0]} size={[0.08, 2.5, 3.5]} color={WALL_WARM} />

                {/* Floor label */}
                <FloorLabel position={[0, 0.04, -1.2]} text="LIVING ROOM" width={1.6} />

                {/* Ceiling recessed lights */}
                <RecessedLight position={[-1.0, 2.4, -1.0]} on={s.livingLights} />
                <RecessedLight position={[1.0, 2.4, -1.0]} on={s.livingLights} />
                <RecessedLight position={[-1.0, 2.4, 0.6]} on={s.livingLights} />
                <RecessedLight position={[1.0, 2.4, 0.6]} on={s.livingLights} />
                <LEDStrip position={[0, 2.35, -1.68]} length={3.7} on={s.livingLights} />

                {/* Ceiling fan */}
                <CeilingFan position={[0.4, 2.15, 0.4]} on={s.livingFan} />

                {/* TV on back wall */}
                <TVSet position={[0, 1.6, -1.68]} on={s.livingTv} />
                {/* Wall art (right of TV) */}
                <WallArt position={[1.4, 1.6, -1.68]} color="#F4A24A" />
                {/* Rug */}
                <Rug position={[0, 0.03, 0.6]} size={[2.6, 1.7]} />
                {/* Sofa facing TV (backrest to +z, cushions to -z toward TV) */}
                <Sofa position={[0, 0.04, 1.0]} rotation={[0, Math.PI, 0]} />
                {/* Coffee table between sofa and TV */}
                <CoffeeTable position={[0, 0.04, 0]} />

                {/* Split AC on left wall */}
                <SplitAC position={[-1.94, 2.1, 0.5]} on={s.livingAc} orient="-x" />
                {/* Window BENEATH the AC on the same left wall */}
                <Window position={[-1.94, 1.05, 0.5]} size={[1.8, 1.0]} orient="-x" />
                <CurtainSet position={[-1.85, 1.62, 0.5]} open={s.livingCurtains} width={2.0} height={1.2} orient="-x" />

                {/* Plant near open right side */}
            </group>

            {/* =====================================================
                KITCHEN (back-left)   x ∈ [-5.5, -1.5],  z ∈ [-3.5, -1]
                Walls on -x and -z
               ===================================================== */}
            <group position={[-4.2, 0, -3.4]}>
                <Floor position={[0, 0, 0]} size={[4, 2.5]} color={FLOOR_KITCHEN} plank={false} />
                <Wall position={[0, 1.25, -1.25]} size={[4, 2.5, 0.08]} color={WALL_KITCHEN} />
                <Wall position={[-2, 1.25, 0]} size={[0.08, 2.5, 2.5]} color={WALL_KITCHEN} />

                <FloorLabel position={[0.8, 0.04, 0.9]} text="KITCHEN" width={1.3} />

                {/* Backsplash tile pattern (subtle) */}
                <mesh position={[0, 1.4, -1.2]}><boxGeometry args={[3.5, 0.9, 0.01]} /><meshStandardMaterial color="#DAE8DA" /></mesh>

                {/* Ceiling lights */}
                <RecessedLight position={[-0.8, 2.4, -0.6]} on={s.livingLights} />
                <RecessedLight position={[0.8, 2.4, -0.6]} on={s.livingLights} />

                {/* Long counter along back wall */}
                <KitchenCounter position={[0.4, 0, -0.9]} size={[3, 0.55]} />
                {/* Cooktop */}
                <GasHob position={[-0.8, 0.9, -0.9]} />
                {/* Sink */}
                <Sink position={[0.9, 0.9, -0.85]} />

                {/* Fridge on left wall */}
                <Fridge position={[-1.5, 0, 0.2]} />

                {/* Window on back wall above sink */}
                <Window position={[0.9, 1.6, -1.2]} size={[1.2, 0.7]} />

                {/* Doorway to hallway (right side opening), hinge away from counter */}

                {/* Ceiling fan */}
                <CeilingFan position={[0, 2.15, 0]} on={s.livingFan} />
            </group>

            {/* =====================================================
                BEDROOM (back-right)   x ∈ [1.5, 5.5],  z ∈ [-3.5, 0]
                Walls on -x and -z
               ===================================================== */}
            <group position={[3.5, 0, -1.75]}>
                <Floor position={[0, 0, 0]} size={[4, 3.5]} color={FLOOR_BED} />
                <Wall position={[0, 1.25, -1.75]} size={[4, 2.5, 0.08]} color={WALL_COOL} />
                <Wall position={[-2, 1.25, 0]} size={[0.08, 2.5, 3.5]} color={WALL_COOL} />

                <FloorLabel position={[0, 0.04, 1.35]} text="BEDROOM" width={1.5} />

                {/* Ceiling lights */}
                <RecessedLight position={[-1.0, 2.4, -1.0]} on={s.bedroomLights} />
                <RecessedLight position={[1.0, 2.4, -1.0]} on={s.bedroomLights} />
                <RecessedLight position={[-1.0, 2.4, 0.6]} on={s.bedroomLights} />
                <RecessedLight position={[1.0, 2.4, 0.6]} on={s.bedroomLights} />

                {/* Ceiling fan */}
                <CeilingFan position={[0.4, 2.15, 0.4]} on={s.bedroomFan} />

                {/* Bed centered against back wall (headboard on -z, so no rotation needed) */}
                <Bed position={[0.4, 0.04, -0.6]} />

                {/* Nightstands beside pillows */}
                <Nightstand position={[-0.75, 0.04, -1.4]} on={s.bedroomLights} />
                <Nightstand position={[1.55, 0.04, -1.4]} on={s.bedroomLights} />

                {/* Rug at foot of bed */}
                <Rug position={[0.4, 0.03, 0.9]} size={[2.2, 1.3]} color="#B49B72" />

                {/* Wardrobe on LEFT wall, doors facing +x (into room) */}
                <Wardrobe position={[-1.65, 0, 0.5]} rotation={[0, Math.PI / 2, 0]} />

                {/* Split AC on left wall, above wardrobe */}
                <SplitAC position={[-1.94, 2.1, -0.9]} on={s.bedroomAc} orient="-x" />

                {/* Window on back wall between bed pillow area (window ABOVE the headboard) */}
                <Window position={[0.4, 1.75, -1.68]} size={[2, 0.75]} />
                <CurtainSet position={[0.4, 2.15, -1.62]} open={s.bedroomCurtains} width={2.2} height={0.95} />

                {/* Wall art near door */}
                <WallArt position={[0.4, 1.05, -1.68]} color={CYAN} />

                {/* Doorway on right side (leads to hall), hinge on +z so it opens away from bed */}
            </group>

            {/* =====================================================
                ENTRANCE PORCH (front-right)   x ∈ [1.5, 5.5],  z ∈ [1, 4]
                An outdoor porch with front door on the back wall (-z).
               ===================================================== */}
            <group position={[4.2, 0, 3.6]}>
                <Floor position={[0, 0, 0]} size={[4, 3]} color={FLOOR_PORCH} />
                {/* Back wall (the wall containing the front door) */}
                <Wall position={[0, 1.25, -1.5]} size={[4, 2.5, 0.1]} color={WALL_WARM} />
                {/* Left half-wall (short — indicates porch railing / entrance side) */}
                <mesh position={[-2, 0.5, 0]}><boxGeometry args={[0.08, 1.0, 3]} /><meshStandardMaterial color="#DAD1BE" /></mesh>

                <FloorLabel position={[0.6, 0.04, 1.0]} text="ENTRANCE" width={1.5} />

                {/* Front door on back wall, offset left */}
                <FrontDoor position={[-0.9, 0.9, -1.45]} locked={s.doorLocked} />
                {/* Doorbell right of door */}
                <Doorbell position={[-0.15, 1.15, -1.42]} on={!s.doorLocked} />
                {/* Camera above door */}
                <SecurityCamera position={[-0.9, 1.9, -1.35]} on={s.camera} />
                {/* Porch light right of door */}
                <PorchLight position={[0.35, 1.6, -1.4]} on={s.entranceLight} />

                {/* Doormat in front of door */}
                <Doormat position={[-0.9, 0.04, -1.0]} />

                {/* House number placard */}
                <mesh position={[0.9, 1.75, -1.44]}>
                    <boxGeometry args={[0.4, 0.16, 0.02]} />
                    <meshStandardMaterial color={NAVY} />
                </mesh>
            </group>

            {/* Connective floor pads showing "hallway" between rooms */}
            <mesh position={[0, -0.08, 0]} receiveShadow>
                <boxGeometry args={[1.2, 0.05, 4.8]} />
                <meshStandardMaterial color="#A17B4B" roughness={0.85} />
            </mesh>
        </Canvas>
    );
}
